import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, BookMarked, Facebook } from "lucide-react";
import { Section } from "@/components/Section";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccessMsg("");
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      const msg = data.message || "Sent.";
      toast.success(msg);
      setSuccessMsg(msg);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not send. Try again.";
      toast.error(typeof msg === "string" ? msg : "Form invalid.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-wrapper pt-32 md:pt-40" data-testid="contact-page">
      <Section>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="md:col-span-5"
          >
            <div className="overline mb-5">Send a Letter</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F4F4F6] font-light leading-[1.05] mb-8">
              Across the void, into the inbox.
            </h1>
            <p className="text-[#A0A4AE] text-base md:text-lg leading-relaxed mb-12 max-w-md">
              For media inquiries, foreign rights, event invitations, or just to tell me which character ruined you — drop a note.
            </p>

            <div className="space-y-7">
              <div className="flex items-start gap-4">
                <Mail size={18} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <div className="overline mb-1">Email</div>
                  <a
                    href="mailto:elliestoneauthor@yahoo.com"
                    data-testid="contact-email-link"
                    className="text-[#F4F4F6] text-sm hover:text-[#D4AF37] transition-colors"
                  >
                    elliestoneauthor@yahoo.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin size={18} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <div className="overline mb-1">Based In</div>
                  <div className="text-[#F4F4F6] text-sm">The deep south, playing with frogs</div>
                </div>
              </div>
              <div className="pt-4">
                <div className="overline mb-4">Find Me Elsewhere</div>
                <div className="flex items-center gap-4">
                  <a
                    href="https://www.goodreads.com/author/show/70183152.Ella_Raine?ref=nav_profile_l"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-goodreads"
                    aria-label="Goodreads"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#F4F4F6]/70 transition-colors text-sm"
                  >
                    <BookMarked size={16} /> Goodreads
                  </a>
                  <a
                    href="https://www.facebook.com/profile.php?id=61589686628261"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-facebook"
                    aria-label="Facebook"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-[#D4AF37]/10 hover:border-[#D4AF37] hover:text-[#D4AF37] text-[#F4F4F6]/70 transition-colors text-sm"
                  >
                    <Facebook size={16} /> Facebook
                  </a>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-7 bg-[#2B3A4A] p-8 md:p-12 rounded-2xl border border-[#D4AF37]/5 space-y-8"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <label className="overline block mb-3" htmlFor="c-name">Name</label>
                <input
                  id="c-name"
                  data-testid="contact-name"
                  required
                  type="text"
                  value={form.name}
                  onChange={set("name")}
                  className="input-line"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="overline block mb-3" htmlFor="c-email">Email</label>
                <input
                  id="c-email"
                  data-testid="contact-email"
                  required
                  type="email"
                  value={form.email}
                  onChange={set("email")}
                  className="input-line"
                  placeholder="you@somewhere.cosmos"
                />
              </div>
            </div>
            <div>
              <label className="overline block mb-3" htmlFor="c-subject">Subject</label>
              <input
                id="c-subject"
                data-testid="contact-subject"
                required
                type="text"
                value={form.subject}
                onChange={set("subject")}
                className="input-line"
                placeholder="Why are you writing?"
              />
            </div>
            <div>
              <label className="overline block mb-3" htmlFor="c-message">Message</label>
              <textarea
                id="c-message"
                data-testid="contact-message"
                required
                rows={6}
                value={form.message}
                onChange={set("message")}
                className="input-line resize-none"
                placeholder="Tell me a story…"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit"
              className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending…" : "Send Message"}
            </button>
            {successMsg && (
              <motion.p
                data-testid="contact-success"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-[#D4AF37] font-mono tracking-wide"
              >
                ✦ {successMsg}
              </motion.p>
            )}
          </motion.form>
        </div>
      </Section>
    </div>
  );
}
